import React from "react";
import { Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Visual badge marking a newly-listed app. Two variants:
 *   - `variant="pill"`  → small kebab pill (used in rankings rows + cards)
 *   - `variant="strip"` → wider hero strip ("Just launched · Listed 3 days ago")
 *                        used on the app-detail page.
 *
 * Falls back gracefully to NULL when the app isn't recently listed so callers
 * can drop it in unconditionally:  {app.recently_listed && <NewBadge ... />}
 */
const NewBadge = ({ app, variant = "pill" }) => {
  const { t } = useLang();
  if (!app || !app.recently_listed) return null;
  const days = app.listed_days_ago ?? 0;

  const badgeLabel = (t?.common?.newBadge) || "NEW";
  const justLaunched = (t?.common?.justLaunched) || "Just launched";
  const listedToday = (t?.common?.listedToday) || "Listed today";
  const listedDaysAgoTpl = (t?.common?.listedDaysAgo) || "Listed {n} days ago";
  const ageLabel =
    days <= 0 ? listedToday : listedDaysAgoTpl.replace("{n}", String(days));

  if (variant === "strip") {
    return (
      <div
        data-testid={`new-strip-${app.slug}`}
        className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-full text-xs font-semibold mb-4"
      >
        <Sparkles size={12} className="fill-emerald-500 text-emerald-500" />
        <span className="uppercase tracking-wider text-[10px] font-bold">
          {justLaunched}
        </span>
        <span className="text-emerald-600 font-normal normal-case">
          {" · "}{ageLabel}
        </span>
      </div>
    );
  }

  // pill (default)
  return (
    <span
      data-testid={`new-badge-${app.slug}`}
      className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm"
    >
      <Sparkles size={10} className="fill-white text-white" />
      {badgeLabel}
    </span>
  );
};

export default NewBadge;
